/**
 * Universal API Error Message Extractor
 * Parses Laravel validation errors, Axios error objects, backend response payloads,
 * and string messages into clean, user-friendly error text for toasts and notifications.
 *
 * @param {any} errorOrResponse - Axios error, response object, error string, or API response
 * @param {string} fallback - Default fallback message if no specific error can be parsed
 * @returns {string} - Clean error message string
 */
export const extractApiErrorMessage = (errorOrResponse, fallback = "An unexpected error occurred. Please try again.") => {
  if (!errorOrResponse) return fallback;

  // 1. If passed directly as a string
  if (typeof errorOrResponse === "string" && errorOrResponse.trim()) {
    const trimmed = errorOrResponse.trim();
    if (isNaN(Number(trimmed)) && !trimmed.toLowerCase().includes("status code")) {
      return trimmed;
    }
  }

  // 2. Extract data payload from Axios response/error or raw object
  const data =
    errorOrResponse?.response?.data !== undefined
      ? errorOrResponse.response.data
      : errorOrResponse?.data !== undefined
      ? errorOrResponse.data
      : errorOrResponse;

  if (typeof data === "string" && data.trim()) {
    const trimmed = data.trim();
    if (isNaN(Number(trimmed)) && !trimmed.toLowerCase().includes("status code")) {
      return trimmed;
    }
  }

  if (typeof data === "object" && data !== null) {
    // 3. Check Laravel standard validation errors: { errors: { field: ["message"] } } or { errors: ["message"] }
    if (data.errors) {
      if (typeof data.errors === "string" && data.errors.trim()) {
        return data.errors.trim();
      }
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        const first = data.errors[0];
        if (typeof first === "string" && first.trim()) return first.trim();
        if (typeof first === "object" && first?.message) return String(first.message).trim();
      }
      if (typeof data.errors === "object") {
        for (const key of Object.keys(data.errors)) {
          const val = data.errors[key];
          if (Array.isArray(val) && val.length > 0) {
            const firstVal = val[0];
            if (typeof firstVal === "string" && firstVal.trim()) return firstVal.trim();
            if (typeof firstVal === "object" && firstVal?.message) return String(firstVal.message).trim();
          }
          if (typeof val === "string" && val.trim()) {
            return val.trim();
          }
        }
      }
    }

    // 4. Check error field (ignoring numeric HTTP status codes like 422, 400, 500)
    if (data.error !== undefined && data.error !== null) {
      if (typeof data.error === "string" && data.error.trim() && isNaN(Number(data.error.trim()))) {
        return data.error.trim();
      }
      if (Array.isArray(data.error) && data.error.length > 0) {
        const first = data.error[0];
        if (typeof first === "string" && first.trim() && isNaN(Number(first.trim()))) return first.trim();
        if (typeof first === "object" && first?.message) return String(first.message).trim();
      }
      if (typeof data.error === "object") {
        for (const key of Object.keys(data.error)) {
          const val = data.error[key];
          if (Array.isArray(val) && val.length > 0 && typeof val[0] === "string" && val[0].trim()) {
            return val[0].trim();
          }
          if (typeof val === "string" && val.trim() && isNaN(Number(val.trim()))) {
            return val.trim();
          }
        }
      }
    }

    // 5. Check direct message fields: message, msg, detail, error_description
    const directMessage =
      data.message ||
      data.msg ||
      data.detail ||
      data.error_description;

    if (
      typeof directMessage === "string" &&
      directMessage.trim() &&
      isNaN(Number(directMessage.trim())) &&
      !directMessage.toLowerCase().includes("status code")
    ) {
      return directMessage.trim();
    }

    // 6. Check action / action_message fields
    if (typeof data.action_message === "string" && data.action_message.trim()) {
      return data.action_message.trim();
    }
    if (
      typeof data.action === "string" &&
      data.action.trim() &&
      isNaN(Number(data.action.trim())) &&
      data.action !== "error" &&
      data.action !== "failed"
    ) {
      return data.action.trim();
    }

    // 7. Nested data.data object inspection
    if (data.data && typeof data.data === "object") {
      const nested = extractApiErrorMessage(data.data, "");
      if (nested && isNaN(Number(nested))) return nested;
    }
  }

  // 8. Check generic error message on Error / AxiosError object
  if (typeof errorOrResponse?.message === "string" && errorOrResponse.message.trim()) {
    const rawMsg = errorOrResponse.message.trim();
    if (!rawMsg.toLowerCase().includes("status code") && !rawMsg.toLowerCase().includes("network error")) {
      return rawMsg;
    }
  }

  return fallback;
};

// Backward-compatible alias
export const getApiErrorMessage = extractApiErrorMessage;

export default extractApiErrorMessage;
